import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, orderBy, limit, startAfter, getDocs, where, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import Tooltip from '../common/Tooltip';

const LOG_COLLECTIONS = [
  { key: 'logsCustomer', label: 'Customer', entityType: 'Customer' },
  { key: 'logsOpp', label: 'Opportunity', entityType: 'Opportunity' },
  { key: 'logsTask', label: 'Task', entityType: 'Task' },
  { key: 'logsContact', label: 'Contact', entityType: 'Contact' },
  { key: 'logsUser', label: 'User', entityType: 'User' },
  { key: 'logsOrder', label: 'Order', entityType: 'Order' },
  { key: 'logsEmail', label: 'Email Campaign', entityType: 'EmailCampaign' },
  { key: 'securityLogs', label: 'Security', entityType: 'Security' },
  { key: 'logsDataSync', label: 'Data Sync', entityType: 'DataSync' },
  { key: 'logsCalendar', label: 'Calendar', entityType: 'CalendarEvent' },
];

const PAGE_SIZE = 50;

const LogViewerTab = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastDocs, setLastDocs] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [usersMap, setUsersMap] = useState(new Map()); // uid/email -> displayName
  const [failedCollections, setFailedCollections] = useState([]);

  // Filters
  const [selectedCollection, setSelectedCollection] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [levelFilter, setLevelFilter] = useState('');

  // Sorting
  const [sortKey, setSortKey] = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');

  // Applied server-side filters (only update on Apply click)
  const [applied, setApplied] = useState({
    collection: 'all',
    dateFrom: '',
    dateTo: '',
    level: '',
  });

  // Load users map for display name enrichment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const snap = await getDocs(collection(db, collectionPath('users')));
        const map = new Map();
        snap.docs.forEach(d => {
          const data = d.data();
          const name = data.displayName
            || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : '')
            || data.name
            || '';
          if (name) {
            map.set(d.id, name);
            if (data.uid && data.uid !== d.id) map.set(data.uid, name);
            if (data.email) {
              map.set(data.email, name);
              map.set(data.email.split('@')[0], name);
            }
          }
        });
        setUsersMap(map);
      } catch (e) {
        console.warn('Failed to load users for display name enrichment:', e);
      }
    };
    loadUsers();
  }, []);

  const collectionsToQuery = useMemo(() => {
    if (applied.collection === 'all') return LOG_COLLECTIONS;
    return LOG_COLLECTIONS.filter(c => c.key === applied.collection);
  }, [applied.collection]);

  const buildQuery = useCallback((colKey, lastDoc = null) => {
    const ref = collection(db, collectionPath(colKey));
    const constraints = [];

    if (applied.level) {
      constraints.push(where('level', '==', applied.level));
    }
    constraints.push(orderBy('timestamp', 'desc'));

    if (applied.dateFrom) {
      // Parse as local date (YYYY-MM-DD input) — split to avoid UTC interpretation
      const [fy, fm, fd] = applied.dateFrom.split('-').map(Number);
      const from = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
      constraints.push(where('timestamp', '>=', Timestamp.fromDate(from)));
    }
    if (applied.dateTo) {
      const [ty, tm, td] = applied.dateTo.split('-').map(Number);
      const to = new Date(ty, tm - 1, td, 23, 59, 59, 999);
      constraints.push(where('timestamp', '<=', Timestamp.fromDate(to)));
    }

    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }
    constraints.push(limit(PAGE_SIZE));

    return query(ref, ...constraints);
  }, [applied]);

  const normalizeLog = useCallback((doc, colKey) => {
    const data = doc.data();
    const collectionInfo = LOG_COLLECTIONS.find(c => c.key === colKey);
    const ts = data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp ? new Date(data.timestamp) : null);
    const isSecurity = colKey === 'securityLogs';

    // Resolve display name: prefer usersMap canonical name, then fall back to stored name
    const uid = data.userId || data.actorId || '';
    const email = data.userEmail || data.actorEmail || '';
    const rawName = data.userName || data.actorName || '';
    const resolvedName = usersMap.get(uid)
      || usersMap.get(email)
      || (email && usersMap.get(email.split('@')[0]))
      || rawName
      || email
      || uid
      || '';

    return {
      id: doc.id,
      _collection: colKey,
      _collectionLabel: collectionInfo?.label || colKey,
      timestamp: ts,
      entityType: data.entityType || collectionInfo?.entityType || '',
      entityId: data.entityId || (isSecurity ? data.actorId : '') || '',
      action: data.action || '',
      level: data.level || 'info',
      userId: uid,
      userName: resolvedName,
      changes: data.changes || [],
      associations: data.associations || {},
      details: data.details || null,
      _raw: data,
    };
  }, [usersMap]);

  const fetchLogs = useCallback(async (append = false) => {
    setLoading(true);
    try {
      const allResults = [];
      const newLastDocs = {};
      const failed = [];

      // One unreadable collection (e.g. a missing rule) must not hide every other log type.
      await Promise.all(collectionsToQuery.map(async ({ key, label }) => {
        try {
          const lastDoc = append ? lastDocs[key] : null;
          const q = buildQuery(key, lastDoc);
          const snapshot = await getDocs(q);

          snapshot.docs.forEach(doc => {
            allResults.push(normalizeLog(doc, key));
          });

          if (snapshot.docs.length > 0) {
            newLastDocs[key] = snapshot.docs[snapshot.docs.length - 1];
          }
        } catch (err) {
          console.error(`Error fetching ${key} logs:`, err);
          failed.push(label);
        }
      }));
      setFailedCollections(failed);

      // Sort all results by timestamp desc
      allResults.sort((a, b) => {
        if (!a.timestamp) return 1;
        if (!b.timestamp) return -1;
        return b.timestamp - a.timestamp;
      });

      if (append) {
        setLogs(prev => [...prev, ...allResults]);
        setLastDocs(prev => ({ ...prev, ...newLastDocs }));
      } else {
        setLogs(allResults);
        setLastDocs(newLastDocs);
      }

      setHasMore(allResults.length >= collectionsToQuery.length);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [collectionsToQuery, lastDocs, buildQuery, normalizeLog]);

  // Fetch when applied filters change (including initial load)
  useEffect(() => {
    fetchLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const handleApply = () => {
    setLastDocs({});
    setHasMore(true);
    setApplied({
      collection: selectedCollection,
      dateFrom,
      dateTo,
      level: levelFilter,
    });
  };

  const handleLoadMore = () => {
    fetchLogs(true);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'timestamp' ? 'desc' : 'asc');
    }
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-indigo-500 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Client-side filtering for action, user, and text search
  const filteredLogs = useMemo(() => {
    let result = logs;
    if (actionFilter) {
      const lower = actionFilter.toLowerCase();
      result = result.filter(l => l.action.toLowerCase().includes(lower));
    }
    if (userFilter) {
      const lower = userFilter.toLowerCase();
      result = result.filter(l =>
        l.userName.toLowerCase().includes(lower) ||
        l.userId.toLowerCase().includes(lower)
      );
    }
    if (searchText) {
      const lower = searchText.toLowerCase();
      result = result.filter(l => {
        if (l.entityId.toLowerCase().includes(lower)) return true;
        if (l.action.toLowerCase().includes(lower)) return true;
        if (l.userName.toLowerCase().includes(lower)) return true;
        if (l.entityType.toLowerCase().includes(lower)) return true;
        if (l.details && l.details.toLowerCase().includes(lower)) return true;
        if (l.changes && JSON.stringify(l.changes).toLowerCase().includes(lower)) return true;
        // Search associations values (customerId, taskId, etc.)
        if (l.associations) {
          const assocValues = Object.values(l.associations).filter(Boolean);
          if (assocValues.some(v => String(v).toLowerCase().includes(lower))) return true;
        }
        // Search deletedEntity fields (entityType, entityId, entityName)
        const de = l._raw?.deletedEntity;
        if (de) {
          if (de.entityName && de.entityName.toLowerCase().includes(lower)) return true;
          if (de.entityId && de.entityId.toLowerCase().includes(lower)) return true;
          if (de.entityType && de.entityType.toLowerCase().includes(lower)) return true;
        }
        return false;
      });
    }
    // Sort
    result = [...result].sort((a, b) => {
      let aVal, bVal;
      switch (sortKey) {
        case 'timestamp':
          aVal = a.timestamp ? a.timestamp.getTime() : 0;
          bVal = b.timestamp ? b.timestamp.getTime() : 0;
          break;
        case 'level':
          aVal = a.level || '';
          bVal = b.level || '';
          break;
        case 'type':
          aVal = a._collectionLabel || '';
          bVal = b._collectionLabel || '';
          break;
        case 'action':
          aVal = a.action || '';
          bVal = b.action || '';
          break;
        case 'entityId':
          aVal = a.entityId || '';
          bVal = b.entityId || '';
          break;
        case 'userName':
          aVal = a.userName || '';
          bVal = b.userName || '';
          break;
        default:
          return 0;
      }
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return result;
  }, [logs, actionFilter, userFilter, searchText, sortKey, sortDir]);

  const getLevelBadge = (level) => {
    const styles = {
      error: 'bg-red-100 text-red-700',
      warn: 'bg-yellow-100 text-yellow-700',
      info: 'bg-blue-100 text-blue-700',
      debug: 'bg-gray-100 text-gray-600',
    };
    return styles[level] || styles.info;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    return ts.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit'
    });
  };

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Log Type</label>
            <select
              value={selectedCollection}
              onChange={e => setSelectedCollection(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Collections</option>
              {LOG_COLLECTIONS.map(c => (
                <option key={c.key} value={c.key}>{c.label} Logs</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
            <input
              type="text"
              value={actionFilter}
              onChange={e => setActionFilter(e.target.value)}
              placeholder="e.g. Created, Updated, Deleted"
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${actionFilter ? 'border-yellow-400' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Level</label>
            <select
              value={levelFilter}
              onChange={e => setLevelFilter(e.target.value)}
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${levelFilter ? 'border-yellow-400' : ''}`}
            >
              <option value="">All Levels</option>
              <option value="info">INFO</option>
              <option value="warn">WARN</option>
              <option value="error">ERROR</option>
              <option value="debug">DEBUG</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">User</label>
            <input
              type="text"
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
              placeholder="Name or email"
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${userFilter ? 'border-yellow-400' : ''}`}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search entity ID, changes, etc."
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${searchText ? 'border-yellow-400' : ''}`}
              onKeyDown={e => e.key === 'Enter' && handleApply()}
            />
          </div>
          <div className="flex items-end gap-2 sm:col-span-2">
            <button
              onClick={handleApply}
              disabled={loading}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              Apply Filters
            </button>
            <button
              onClick={() => {
                setSelectedCollection('all');
                setDateFrom('');
                setDateTo('');
                setActionFilter('');
                setLevelFilter('');
                setUserFilter('');
                setSearchText('');
                setSortKey('timestamp');
                setSortDir('desc');
                setLastDocs({});
                setHasMore(true);
                setApplied({ collection: 'all', dateFrom: '', dateTo: '', level: '' });
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex justify-between items-center mb-2">
        <p className="text-sm text-gray-600">
          Showing <span className="font-semibold">{filteredLogs.length}</span> log entries
          {logs.length !== filteredLogs.length && (
            <span className="text-gray-400"> (filtered from {logs.length})</span>
          )}
        </p>
        {loading && (
          <span className="text-sm text-indigo-600 animate-pulse">Loading...</span>
        )}
      </div>

      {failedCollections.length > 0 && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Could not load: {failedCollections.join(', ')}. Other log types are shown. See the browser console for details.
        </div>
      )}

      {/* Log Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort('timestamp')}>Timestamp{sortIndicator('timestamp')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort('level')}>Level{sortIndicator('level')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort('type')}>Type{sortIndicator('type')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort('action')}>Action{sortIndicator('action')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort('entityId')}>Entity ID{sortIndicator('entityId')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort('userName')}>User{sortIndicator('userName')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-500">
                    {logs.length === 0 ? 'No logs found. Try adjusting your filters.' : 'No logs match the current filters.'}
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <React.Fragment key={`${log._collection}-${log.id}`}>
                    <tr
                      className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${expandedRow === `${log._collection}-${log.id}` ? 'bg-indigo-50' : ''}`}
                      onClick={() => setExpandedRow(expandedRow === `${log._collection}-${log.id}` ? null : `${log._collection}-${log.id}`)}
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 font-mono text-xs">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLevelBadge(log.level)}`}>
                          {log.level?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                          {log._collectionLabel}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-800 font-medium">{log.action || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600 font-mono text-xs max-w-[200px] truncate" title={log.entityId}>
                        {log.entityId || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 max-w-[180px] truncate" title={log.userName}>
                        {log.userName || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-400">
                        {expandedRow === `${log._collection}-${log.id}` ? '▲' : '▼'}
                      </td>
                    </tr>

                    {/* Expanded Details */}
                    {expandedRow === `${log._collection}-${log.id}` && (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500 text-xs font-medium mb-1">Document ID</p>
                              <p className="text-gray-700 font-mono text-xs break-all">{log.id}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs font-medium mb-1">Collection</p>
                              <p className="text-gray-700">{log._collection}</p>
                            </div>
                            {log.userId && (
                              <div>
                                <p className="text-gray-500 text-xs font-medium mb-1">User ID</p>
                                <p className="text-gray-700 font-mono text-xs">{log.userId}</p>
                              </div>
                            )}
                            {log.details && (
                              <div className="sm:col-span-2">
                                <p className="text-gray-500 text-xs font-medium mb-1">Details</p>
                                <pre className="text-gray-700 text-xs bg-white p-2 rounded border overflow-x-auto whitespace-pre-wrap">
                                  {typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log.changes && log.changes.length > 0 && (
                              <div className="sm:col-span-2">
                                <p className="text-gray-500 text-xs font-medium mb-1">Changes ({log.changes.length})</p>
                                <div className="space-y-1">
                                  {log.changes.map((change, i) => (
                                    <div key={i} className="text-xs bg-white p-2 rounded border">
                                      <span className="font-medium text-gray-800">{change.field || change.key}:</span>{' '}
                                      <span className="text-red-600 line-through">{String(change.oldValue ?? change.from ?? '')}</span>
                                      {' → '}
                                      <span className="text-green-600">{String(change.newValue ?? change.to ?? '')}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {log.associations && Object.keys(log.associations).length > 0 && (
                              <div className="sm:col-span-2">
                                <p className="text-gray-500 text-xs font-medium mb-1">Associations</p>
                                <pre className="text-gray-700 text-xs bg-white p-2 rounded border overflow-x-auto whitespace-pre-wrap">
                                  {JSON.stringify(log.associations, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Load More */}
        {hasMore && filteredLogs.length > 0 && (
          <div className="p-4 border-t border-gray-200 text-center">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogViewerTab;
